import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { AppRoutes, DrillScoring, HapticsService, SessionRoutes, ToastService } from '@core';
import {
  SZ_SCORING_LABELS,
  SzAppShellComponent,
  SzButtonComponent,
  SzCardComponent,
  SzConfirmDialogService,
  SzCtaBarComponent,
  SzIcCircleComponent,
  SzIconButtonComponent,
  SzIconComponent,
  SzNumBigComponent,
  SzPageTitleComponent,
  SzPillComponent,
  SzRowCardComponent,
  SzSectionHeadComponent,
  SzSuccessOverlayService,
  SzTileComponent,
  SzTopBarComponent
} from '@ui';
import {
  PlanDeletedError,
  SaveSessionCommandHandler,
  SaveSessionCommandResult,
  SessionDraftQueryHandler,
  SessionDraftQueryResult
} from '../../application/handlers';
import { RunViewModel, SessionDrillViewModel } from '../../application/models';
import { SessionStore } from '../../application/state';
import { SessionRepListComponent } from '../session-rep-list/session-rep-list.component';
import { buildRunRows, formatBestValue } from '../session-rep-list/run-rows.util';
import { SessionRunRow } from '../session-rep-list/session-rep-list.types';

type TimelineStatus = 'done' | 'next' | 'queue';

interface TimelineEntry {
  readonly id: string;
  readonly instanceId: string;
  readonly index: number;
  readonly drillName: string;
  readonly scoringLabel: string;
  readonly runsCount: number;
  readonly bestValue: string;
  readonly showBestLabel: boolean;
  readonly status: TimelineStatus;
  readonly runRows: readonly SessionRunRow[];
  readonly removable: boolean;
}

interface CompletedTile {
  readonly label: string;
  readonly value: string;
  readonly unit: string | null;
  readonly variant: 'default' | 'lime';
}

@Component({
  selector: 'lib-session-drill-completed',
  templateUrl: './session-drill-completed.component.html',
  styleUrls: ['./session-drill-completed.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    SzAppShellComponent,
    SzTopBarComponent,
    SzPageTitleComponent,
    SzNumBigComponent,
    SzCardComponent,
    SzPillComponent,
    SzTileComponent,
    SzRowCardComponent,
    SzIcCircleComponent,
    SzSectionHeadComponent,
    SzCtaBarComponent,
    SzButtonComponent,
    SzIconComponent,
    SzIconButtonComponent,
    SessionRepListComponent
  ],
  providers: [SessionDraftQueryHandler, SaveSessionCommandHandler]
})
export class SessionDrillCompletedComponent {
  private readonly router: Router = inject(Router);
  private readonly store: SessionStore = inject(SessionStore);
  private readonly draftHandler: SessionDraftQueryHandler = inject(SessionDraftQueryHandler);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly saveHandler: SaveSessionCommandHandler = inject(SaveSessionCommandHandler);
  private readonly successOverlay: SzSuccessOverlayService = inject(SzSuccessOverlayService);
  private readonly haptics: HapticsService = inject(HapticsService);
  private readonly toast: ToastService = inject(ToastService);
  private readonly confirmDialog: SzConfirmDialogService = inject(SzConfirmDialogService);

  readonly canAddDrill: Signal<boolean> = this.store.canAddDrill;

  readonly draft: Signal<SessionDraftQueryResult | null | undefined> = toSignal(this.draftHandler.query());

  readonly planTitle: Signal<string | null> = computed(() => this.draft()?.draft.planTitle ?? null);
  readonly sessionDrills: Signal<readonly SessionDrillViewModel[]> = computed(
    () => this.draft()?.draft.sessionDrills ?? []
  );
  readonly completedIndex: Signal<number> = computed(() => this.draft()?.draft.activeIndex ?? 0);
  readonly totalDrills: Signal<number> = computed(() => this.sessionDrills().length);

  readonly completedDrill: Signal<SessionDrillViewModel | null> = computed(() => {
    const drills: readonly SessionDrillViewModel[] = this.sessionDrills();
    const idx: number = this.completedIndex();
    return idx >= drills.length ? null : drills[idx];
  });

  readonly nextDrill: Signal<SessionDrillViewModel | null> = computed(() => {
    const drills: readonly SessionDrillViewModel[] = this.sessionDrills();
    const idx: number = this.completedIndex();
    return idx + 1 >= drills.length ? null : drills[idx + 1];
  });

  readonly hasPreviousDrill: Signal<boolean> = computed(() => this.completedIndex() > 0);

  readonly previousDrillName: Signal<string | null> = computed(() => {
    const idx: number = this.completedIndex();
    return idx <= 0 ? null : (this.sessionDrills()[idx - 1]?.drillName ?? null);
  });

  readonly completedPosition: Signal<string> = computed(() => (this.completedIndex() + 1).toString());

  readonly progressPercent: Signal<number> = computed(() => {
    const total: number = this.totalDrills();
    if (total === 0) return 0;
    return Math.round(((this.completedIndex() + 1) / total) * 100);
  });

  readonly completedTiles: Signal<readonly CompletedTile[]> = computed((): readonly CompletedTile[] => {
    const sd: SessionDrillViewModel | null = this.completedDrill();
    if (sd === null) return [];
    const runs: readonly RunViewModel[] = sd.runs;
    switch (sd.scoring) {
      case DrillScoring.TimePerRep: {
        const times: readonly number[] = runs
          .map((r: RunViewModel) => r.timeSeconds)
          .filter((v: number | null): v is number => v !== null);
        const best: number | null = times.length === 0 ? null : Math.min(...times);
        const avg: number | null =
          times.length === 0 ? null : times.reduce((a: number, b: number) => a + b, 0) / times.length;
        return [
          { label: 'Best', value: best === null ? '-' : best.toFixed(2), unit: 's', variant: 'lime' },
          { label: 'Avg', value: avg === null ? '-' : avg.toFixed(2), unit: 's', variant: 'default' },
          { label: 'Runs', value: runs.length.toString(), unit: null, variant: 'default' }
        ];
      }
      case DrillScoring.RepsOnly: {
        const counts: readonly number[] = runs
          .map((r: RunViewModel) => r.reps)
          .filter((v: number | null): v is number => v !== null);
        const total: number | null = counts.length === 0 ? null : counts.reduce((a: number, b: number) => a + b, 0);
        return [
          {
            label: this.translate.instant('session.repsLabel'),
            value: total === null ? '-' : total.toString(),
            unit: null,
            variant: 'lime'
          },
          { label: 'Runs', value: runs.length.toString(), unit: null, variant: 'default' }
        ];
      }
      case DrillScoring.HitFactor: {
        const hfs: readonly number[] = runs
          .map((r: RunViewModel) => r.hitFactor)
          .filter((v: number | null): v is number => v !== null);
        const best: number | null = hfs.length === 0 ? null : Math.max(...hfs);
        const avg: number | null =
          hfs.length === 0 ? null : hfs.reduce((a: number, b: number) => a + b, 0) / hfs.length;
        return [
          { label: 'Best HF', value: best === null ? '-' : best.toFixed(2), unit: null, variant: 'lime' },
          { label: 'Avg', value: avg === null ? '-' : avg.toFixed(2), unit: null, variant: 'default' },
          { label: 'Runs', value: runs.length.toString(), unit: null, variant: 'default' }
        ];
      }
    }
  });

  readonly scoringLabelOf: typeof scoringLabelOf = scoringLabelOf;

  readonly timeline: Signal<readonly TimelineEntry[]> = computed((): readonly TimelineEntry[] => {
    const drills: readonly SessionDrillViewModel[] = this.sessionDrills();
    const completed: number = this.completedIndex();
    const repsUnit: string = this.translate.instant('session.numpad.repsUnit');
    return drills.map((sd: SessionDrillViewModel, i: number): TimelineEntry => {
      let status: TimelineStatus;
      if (i <= completed) status = 'done';
      else if (i === completed + 1) status = 'next';
      else status = 'queue';
      return {
        id: `${sd.drillId ?? 'pos'}-${i}`,
        instanceId: sd.instanceId,
        index: i + 1,
        drillName: sd.drillName,
        scoringLabel: scoringLabelOf(sd.scoring),
        runsCount: sd.runs.length,
        bestValue: formatBestValue(sd, repsUnit),
        showBestLabel: sd.scoring !== DrillScoring.RepsOnly,
        status,
        runRows: buildRunRows(sd, repsUnit),
        removable: sd.addedInSession && i > completed && sd.runs.length === 0
      };
    });
  });

  private readonly expandedIds: WritableSignal<ReadonlySet<string>> = signal<ReadonlySet<string>>(new Set());

  isExpanded(id: string): boolean {
    return this.expandedIds().has(id);
  }

  toggleEntry(id: string): void {
    this.expandedIds.update((current: ReadonlySet<string>): ReadonlySet<string> => {
      const next: Set<string> = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  continueSession(): void {
    this.store.advanceToNextDrill();
    void this.router.navigate(['/', AppRoutes.SESSION, SessionRoutes.ACTIVE]);
  }

  goToAddDrill(): void {
    void this.router.navigate(['/', AppRoutes.SESSION, SessionRoutes.ADD_DRILL]);
  }

  confirmRemoveEntry(entry: TimelineEntry): void {
    this.confirmDialog
      .confirm$({
        header: this.translate.instant('session.drillCompleted.removeDrillConfirm', { drill: entry.drillName }),
        okLabel: this.translate.instant('session.drillCompleted.removeDrillCta'),
        cancelLabel: this.translate.instant('session.drillCompleted.removeDrillCancelCta'),
        destructive: true
      })
      .pipe(
        filter((ok: boolean): boolean => ok),
        take(1)
      )
      .subscribe((): void => this.store.removeAddedDrill(entry.instanceId));
  }

  goToPreviousDrill(): void {
    this.store.goBackToPreviousDrill();
    void this.router.navigate(['/', AppRoutes.SESSION, SessionRoutes.ACTIVE]);
  }

  finishSession(): void {
    this.successOverlay
      .showDuring$<SaveSessionCommandResult>(this.saveHandler.execute(), {
        loadingTitle: this.translate.instant('session.save.saving'),
        successTitle: this.translate.instant('session.save.saved'),
        onSuccess: (): void => this.haptics.impact('success')
      })
      .pipe(take(1))
      .subscribe({
        next: (r: SaveSessionCommandResult) => {
          void this.router.navigate(['/', AppRoutes.SESSION, SessionRoutes.SUMMARY, r.sessionId], {
            queryParams: { completed: 'true' },
            replaceUrl: true
          });
        },
        error: (err: unknown) => {
          if (err instanceof PlanDeletedError) {
            this.toast.presentToast({
              message: this.translate.instant('session.save.plan409Toast'),
              duration: 3000
            });
            this.store.clear();
            void this.router.navigate(['/', AppRoutes.DRILLS]);
            return;
          }
          this.toast.presentToast({
            message: this.translate.instant('session.save.offlineToast'),
            duration: 3000
          });
        }
      });
  }
}

const scoringLabelOf = (scoring: DrillScoring): string => SZ_SCORING_LABELS[scoring];
